import React, { ReactElement, useEffect, useState } from "react";
import { Prompt, Redirect, useHistory, useParams } from "react-router-dom";

import { JobsApi } from "@common/api/v1.1/actions/JobsApi";
import { JobCheckinStatus, JobWarning } from "@common/api/v1.1/types/jobs";
import { ErrorBoundary } from "../../util/ErrorBoundary";
import { Loading } from "../../util/Loading";
import { Actions, ChildrenArgs, JobState, LoggedInState, State } from "../types";
import { Notes } from "./Notes";
import { useJobLoader } from "./useJobLoader";
import { useKeepJobCheckedOut } from "./useKeepJobCheckedOut";

type Props = Readonly<{
  addNote: Actions["addNote"];
  resolveWarning: Actions["resolveWarning"];
  authState: LoggedInState;
  clearActiveJob: Actions["clearActiveJob"];
  setActiveJob: Actions["setActiveJob"];
  deleteNote: Actions["deleteNote"];
  fetchNotes: Actions["fetchNotes"];
  jobState: State["job"];
  activeJobId: State["activeJobId"]
  loadJob: Actions["loadJob"];
  loadRoles: Array<string>;
  refreshCheckout: Actions["refreshCheckout"];
  removeJobFromList: Actions["removeJobFromList"];
  render: (args: ChildrenArgs) => ReactElement;
  review: boolean;
  showMessage: Actions["showMessage"];
  sidebarVisible: boolean;
  updateNote: Actions["updateNote"];
}>;
export const JobManager = ({
  addNote,
  resolveWarning,
  authState,
  clearActiveJob,
  setActiveJob,
  deleteNote,
  fetchNotes,
  jobState,
  activeJobId,
  loadJob,
  loadRoles,
  refreshCheckout,
  removeJobFromList,
  render,
  review,
  showMessage,
  sidebarVisible,
  updateNote,
}: Props) => {
  const { jobId } = useParams();
  useEffect(() => {
    setActiveJob(jobId)
  }, [jobId]);
  useJobLoader(clearActiveJob, loadJob, jobState, activeJobId, loadRoles, review);
  useKeepJobCheckedOut(jobState.state, refreshCheckout);
  const history = useHistory();

  const [noteCount, setNoteCount] = useState(0);
  if (
    jobState.state === JobState.failed ||
    jobState.state === JobState.aborted ||
    (jobState.state !== JobState.initial && jobState.authString !== authState.authString)
  ) {
    return <Redirect to="/" />;
  }
  if (jobState.state !== JobState.ready) {
    return <Loading text="Loading job details" />;
  }
  const notes = (
    <Notes
      addNote={addNote}
      deleteNote={deleteNote}
      fetchNotes={fetchNotes}
      job={jobState.job}
      onUpdateNoteCount={setNoteCount}
      updateNote={updateNote}
      user={authState.user}
    />
  );
  return (
    <ErrorBoundary>
      <Prompt message="Are you sure you want to leave this job?" />
      {render({
        actions: {
          resolveWarning : (warning: JobWarning): Promise<void> => {
            return resolveWarning(warning);
          },
          checkinJob: async (): Promise<void> => {
            if (jobState.state !== JobState.ready) {
              return;
            }
            try {
              const jobsApi = new JobsApi(authState);
              await jobsApi.checkinJob(jobState.job.id, JobCheckinStatus.COMPLETED);

              removeJobFromList(jobState.job.id);
              history.push("/");
              showMessage(
                <>Job "{jobState.job.name}" submitted successfully.</>,
                "Job Submitted.",
                3,
                "success",
              );
            } catch (err) {
              showMessage(<>Job failed to check-in.</>, "Job Submission Failed.", 3, "danger");
              throw err;
            }
          },
          saveContent: async (
            role: string,
            order: number,
            content: string,
            isNew: boolean,
            type?: string,
            name?: string,
            doShowMessage: boolean = true,
          ): Promise<void> => {
            if (jobState.state !== JobState.ready) {
              return;
            }
            const jobsApi = new JobsApi(authState);
            try {
              await jobsApi.saveContent(
                jobState.job.id,
                role,
                order,
                content,
                isNew,
                type,
                name,
              );
              if (!!doShowMessage) {
                showMessage(<>Content saved successfully.</>, "Content saved.", 3, "success");
              }
            } catch (err) {
              showMessage(
                <>Content failed to save. Please try again.</>,
                "Content failed to save.",
                undefined,
                "danger",
              );
              throw err;
            }
          },
          showMessage,
        },
        auth: {
          authString: authState.authString,
          token: authState.token,
          user: authState.user,
        },
        contents: jobState.contents,
        job: jobState.job,
        noteCount,
        notes,
        sidebarVisible,
      })}
    </ErrorBoundary>
  );
};
JobManager.displayName = "JobManager";
